import { ApiError } from '../work/gateway.js';

/**
 * The browser's calls to the Stage 7 endpoints.
 *
 * SAME DISCIPLINE AS STAGES 5 AND 6, and the same two things it does not do.
 * Nothing here names a tenant, because there is nothing it could name one with
 * — the server takes the tenant from the session cookie and ignores anything a
 * request says about it. And nothing here filters, re-checks or hides: the
 * drawer renders what these calls return, and what they return has already been
 * through `accessPredicate` in SQL.
 *
 * ASKING CARRIES AN IDEMPOTENCY KEY generated HERE rather than on the server,
 * for the Stage 5 reason: a key the server minted would be a different key on
 * every retry, which is the same as having none. It matters more here than
 * anywhere else in the product, because a retried question that reached the
 * provider twice would be billed twice.
 */

export interface Citation {
  readonly resource: string;
  readonly recordId: string;
  readonly title: string;
  readonly moduleKey: string;
}

export interface Proposal {
  readonly proposalId: string;
  readonly actionKey: string;
  readonly permissionKey: string;
  readonly summary: string;
  readonly rationale: string;
}

/**
 * A refusal is a RESULT, not an error, and the client mirrors that.
 *
 * The API answers 200 with an outcome, so the drawer branches on the outcome
 * rather than on a status code. Collapsing "you have no budget" and "the
 * network is down" into one catch block is how a person asking a question ends
 * up looking at a stack trace.
 */
export type AskAnswer =
  | {
      readonly outcome: 'answered';
      readonly requestId: string;
      readonly conversationId: string;
      readonly answer: string;
      readonly citations: readonly Citation[];
      readonly groundingScore: number;
      readonly proposals: readonly Proposal[];
    }
  | {
      readonly outcome: 'refused' | 'failed';
      readonly requestId: string;
      readonly conversationId?: string;
      readonly reason: string;
      readonly message: string;
    };

export interface ConversationSummary {
  readonly id: string;
  readonly title: string;
  readonly updatedAt: string;
}

export interface ConversationMessage {
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly body: string;
  readonly requestId: string | null;
  readonly createdAt: string;
}

export interface AiGateway {
  ask(question: string, conversationId?: string): Promise<AskAnswer>;
  act(question: string): Promise<AskAnswer>;
  conversations(): Promise<readonly ConversationSummary[]>;
  conversation(id: string): Promise<{
    readonly conversation: ConversationSummary;
    readonly messages: readonly ConversationMessage[];
  }>;
  confirm(
    proposalId: string,
  ): Promise<{ readonly executionStatus: string; readonly detail: string | null }>;
  reject(proposalId: string): Promise<void>;
}

function newKey(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ?? `${String(Date.now())}-${String(Math.random()).slice(2)}`;
}

export function createAiGateway(baseUrl = '/api/v1'): AiGateway {
  async function call<T>(path: string, init?: RequestInit & { readonly key?: string }): Promise<T> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (init?.key !== undefined) headers['idempotency-key'] = init.key;

    const response = await fetch(`${baseUrl}${path}`, {
      credentials: 'same-origin',
      ...init,
      headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) },
    });

    if (!response.ok) {
      // RFC 7807 from the API, read rather than guessed — so a 404 and a 403
      // reach the screen as the API worded them. The browser must not decide
      // which of the two this was: that decision is the server's.
      const problem = (await response.json().catch(() => ({}))) as {
        title?: string;
        detail?: string;
      };
      throw new ApiError(response.status, problem.title ?? 'Something went wrong', problem.detail);
    }
    return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
  }

  return {
    async ask(question, conversationId) {
      return call<AskAnswer>('/ai/ask', {
        method: 'POST',
        key: newKey(),
        body: JSON.stringify({
          question,
          ...(conversationId === undefined ? {} : { conversationId }),
        }),
      });
    },
    async act(question) {
      return call<AskAnswer>('/ai/act', {
        method: 'POST',
        key: newKey(),
        body: JSON.stringify({ question }),
      });
    },
    async conversations() {
      const body = await call<{ conversations: readonly ConversationSummary[] }>(
        '/ai/conversations',
      );
      return body.conversations;
    },
    async conversation(id) {
      return call<{
        conversation: ConversationSummary;
        messages: readonly ConversationMessage[];
      }>(`/ai/conversations/${id}`);
    },
    async confirm(proposalId) {
      return call<{ executionStatus: string; detail: string | null }>(
        `/ai/proposals/${proposalId}/confirm`,
        { method: 'POST', key: newKey(), body: JSON.stringify({}) },
      );
    },
    async reject(proposalId) {
      await call(`/ai/proposals/${proposalId}/reject`, {
        method: 'POST',
        key: newKey(),
        body: JSON.stringify({}),
      });
    },
  };
}
